/**
 * @file task.tsx
 * @description Компонент страницы очереди задач для управления сущностями очереди задач для рабочего 
 * процесса проекта. Предоставляет комплексную визуализацию очереди задач с иерархической организацией задач 
 * по спринтам и сервисам. Включает продвинутое отслеживание прогресса, управление приоритетами, индикаторы 
 * итераций жизненного цикла, отслеживание задержек и назначение ответственности. Включает специализированную 
 * отрисовку сгруппированных элементов с визуальной иерархией, полосы прогресса с цветовым кодированием 
 * статуса и управление датами с индикаторами задержек.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies ['@utils/table-view', '@features/filter', '@features/filter/types', 'react-dom/server', '@src/components/icons/priority-cell-header', '@components/icons/life-cycle', 'classnames']
 * @related-files ['@features/filter-modal/forms/queue-task/index.tsx']
 */

import { getDefaultCell, getDateTimeCell, getPriorityCell, getDoubleCell, getProgressBar } from '@utils/table-view';
import { QueueTaskFilterForm } from '@features/filter';
import { filterComponent } from '@features/filter/types';
import { renderToStaticMarkup } from 'react-dom/server';
import { PriorityCellHeader } from '@src/components/icons/priority-cell-header';
import { LifeCycleIcon } from '@components/icons/life-cycle';
import cn from 'classnames';
import { TBaseObjectDto } from '../../types';

export const title = 'Очередь задач';
export const searchModalTitle = 'Выбор из очереди задач';
export const type = 'Task';
export const path = 'queue-task';
export const filterModalForm = QueueTaskFilterForm;

export const columns = [
    {
        id: '$item',
        header: [{ text: 'Код' }],
        width: 110,
        render: (cellValue: any, { $type }: any) => (
            <div className={cn({ 'pl-[2.5405vmin]': $type && $type === 'Service' })}>{getDefaultCell(cellValue)}</div>
        ),
        cell: {
            colspan: ({ name }: any) => (name === undefined ? 9 : 1),
            css: ({ $type }: any) =>
                cn('text-[1.3761vmin] leading-[150%] cursor-default hover:!text-black text-medium', {
                    'bg-queue-level-1': $type && $type === 'Sprint',
                    'bg-queue-level-2': $type && $type === 'Service'
                })
        }
    },
    {
        id: 'name',
        header: [{ text: 'Описание' }],
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'priority',
        header: [{ text: renderToStaticMarkup(<PriorityCellHeader />), htmlEnable: true }],
        width: 32,
        align: 'center',
        render: (cellValue: any) => getPriorityCell(cellValue)
    },
    {
        id: 'lifeCycleIteration',
        header: [{ text: renderToStaticMarkup(<LifeCycleIcon />), htmlEnable: true }],
        width: 32,
        align: 'center',
        render: (cellValue: any) => (
            <div
                title={cellValue}
                className={cn('w-[1.0585vmin] h-[1.0585vmin] rounded-full relative', {
                    'bg-success': cellValue === 1,
                    'bg-warning': cellValue === 2,
                    'bg-danger': cellValue > 2
                })}
            />
        )
    },
    {
        id: 'state',
        header: [{ text: 'Состояние' }],
        width: 130,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'progressFact',
        header: [{ text: 'Статус' }],
        width: 120,
        render: (progressFact: any, { progressPlan }: any) => {
            let color: 'success' | 'warning' | 'danger' = 'success';

            if (progressFact < progressPlan && progressPlan - progressFact > 15) {
                color = progressPlan - progressFact < 30 ? 'warning' : 'danger';
            }

            return getProgressBar(progressFact, color);
        },
        cell: { css: () => 'p-[0.3088vmin]' }
    },
    {
        id: 'dateStart',
        header: [{ text: 'Начало' }],
        width: 125,
        render: (cellValue: number, { delayStart }: any) =>
            getDoubleCell(
                {
                    value: getDateTimeCell(cellValue, 'dd DD.MM.YY'),
                    className: 'text-end min-w-[9.3237vmin] w-[9.3237vmin]'
                },
                {
                    value: delayStart ? `${delayStart}д` : null,
                    className: 'text-[#f00] w-[3.0964vmin] min-w-[3.0964vmin]'
                }
            ),
        cell: { css: (_row: any) => 'px-0' }
    },
    {
        id: 'dateEnd',
        header: [{ text: 'Окончание' }],
        width: 125,
        render: (cellValue: number, { delayEnd }: any) =>
            getDoubleCell(
                {
                    value: getDateTimeCell(cellValue, 'dd DD.MM.YY'),
                    className: 'text-end min-w-[9.3237vmin] w-[9.3237vmin]'
                },
                { value: delayEnd ? `${delayEnd}д` : null, className: 'text-[#f00] w-[3.0964vmin] min-w-[3.0964vmin]' }
            ),
        cell: { css: (_row: any) => 'px-0' }
    },
    {
        id: 'executor',
        header: [{ text: 'Ответственный' }],
        width: 190,
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];

export const getFilterControls = (data: TBaseObjectDto = {}): filterComponent[] => [
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Проект'
        },
        name: 'project',
        type: 'Project',
        value: data.project,
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Сервис',
            type: 'Service'
        },
        value: data.service,
        name: 'service',
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Ответственный',
            type: 'User'
        },
        value: data.executor,
        name: 'executor',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Статус'
        },
        type: 'ETaskState',
        value: data.state,
        name: 'state',
        tab: 'filters'
    }
];
